import type { MechtaIDLoginOptions, MechtaIDProfile, MechtaIDUser } from '@mechta-dev/mechta-id-js'
import { MechtaID } from '@mechta-dev/mechta-id-js'
import { pinia } from 'boot/pinia'
import { useAuthStore } from 'stores/auth'

function configuredValue(
  runtimeValue: string | undefined,
  buildValue: string | undefined,
) {
  const value = runtimeValue?.trim() || buildValue?.trim()
  if (value) return value
  return ''
}

function required(name: string, value: string) {
  if (!value) throw new Error(`${name} is required`)
  return value
}

const authority = required(
  'OIDC_ISSUER',
  configuredValue(window.OIDC_ISSUER, import.meta.env.VITE_OIDC_ISSUER),
)
const clientId = required(
  'OIDC_CLIENT_ID',
  configuredValue(window.OIDC_CLIENT_ID, import.meta.env.VITE_OIDC_CLIENT_ID),
)
const scope = configuredValue(
  window.OIDC_SCOPE,
  import.meta.env.VITE_OIDC_SCOPE,
) || 'openid profile roles phone attributes'

const redirectUri = `${window.location.origin}/auth/callback`
const postLogoutRedirectUri = `${window.location.origin}/`
const mechtaID = new MechtaID({
  authority,
  clientId,
  redirectUri,
  postLogoutRedirectUri,
  scope,
})

let initializePromise: Promise<boolean> | null = null

function authStore() {
  return useAuthStore(pinia)
}

function safeReturnPath(value: string) {
  return value.startsWith('/') && !value.startsWith('//') ? value : '/'
}

function authErrorMessage(error: unknown) {
  if (error instanceof TypeError) {
    return 'Не удалось связаться с Mechta ID. Проверьте подключение и попробуйте снова. Если ошибка повторяется, обратитесь к администратору.'
  }
  return error instanceof Error ? error.message : 'Не удалось выполнить вход через Mechta ID'
}

function currentHashRoute() {
  const hash = window.location.hash
  return hash.startsWith('#/') ? safeReturnPath(hash.slice(1)) : '/'
}

function isAuthorizationCallback() {
  const url = new URL(window.location.href)
  const callbackPath = new URL(redirectUri).pathname
  return url.pathname === callbackPath
    && url.searchParams.has('state')
    && (url.searchParams.has('code') || url.searchParams.has('error'))
}

function restoreHashRoute(returnTo: string) {
  const target = new URL(window.location.origin)
  target.pathname = '/'
  target.hash = safeReturnPath(returnTo)
  window.history.replaceState({}, '', `${target.pathname}${target.search}${target.hash}`)
}

class MechtaIDAuthService {
  constructor() {
    mechtaID.onAuthRefreshSuccess = () => this.syncStore()
    mechtaID.onAuthLogout = () => authStore().$reset()
  }

  initialize(): Promise<boolean> {
    if (!initializePromise) {
      initializePromise = this.initializeSession()
    }
    return initializePromise
  }

  async login(returnTo = currentHashRoute()) {
    return this.startLogin({ returnTo: safeReturnPath(returnTo) })
  }

  async loginAsAnotherUser(returnTo = currentHashRoute()) {
    return this.startLogin({
      returnTo: safeReturnPath(returnTo),
      prompt: 'login',
    })
  }

  async logout() {
    authStore().$reset()
    try {
      await mechtaID.logout({ redirectUri: postLogoutRedirectUri })
    } catch (error) {
      await mechtaID.clearToken()
      throw error
    }
  }

  async getAccessToken() {
    await this.initialize()
    const token = await mechtaID.getToken()
    this.syncStore()
    return token
  }

  /**
   * Профиль текущего пользователя (claims из userinfo).
   * oidc-client-ts сконфигурирован с loadUserInfo: true и уже подмешал userinfo
   * в mechtaID.profile — читаем готовое, чтобы не делать второй такой же запрос.
   * loadUserInfo() — фолбэк на случай, если профиль почему-то не наполнен.
   */
  async getProfile(): Promise<MechtaIDProfile> {
    await this.initialize()
    return mechtaID.profile ?? mechtaID.loadUserInfo()
  }

  async refreshAccessToken() {
    await this.initialize()
    await mechtaID.updateToken(Number.MAX_SAFE_INTEGER)
    const token = await mechtaID.getToken(0)
    this.syncStore()
    return token
  }

  /**
   * Терминальный 401: refresh не помог или сервер не принял свежий токен.
   * Чистим сессию БЕЗ повторного входа — при живой SSO-сессии `login()` здесь
   * ушёл бы в бесконечный редирект (Mechta ID молча выдаёт такой же токен).
   * Экран входа открывает guard роутера или кнопка «Попробовать снова».
   * @param authError код причины для `AuthPage` (напр. `access_denied`).
   */
  async endSession(authError = '') {
    await mechtaID.clearToken()
    const store = authStore()
    store.clearSSO()
    if (authError) store.setAuthError(authError)
    initializePromise = Promise.resolve(false)
  }

  private async startLogin(options: MechtaIDLoginOptions): Promise<boolean> {
    authStore().setAuthError('')
    try {
      await mechtaID.login(options)
      return true
    } catch (error) {
      authStore().setAuthError(authErrorMessage(error))
      return false
    }
  }

  private async initializeSession() {
    const store = authStore()
    try {
      if (isAuthorizationCallback()) {
        const result = await mechtaID.handleCallback()
        store.setSSOUser(result.user)
        restoreHashRoute(result.returnTo)
      } else {
        await mechtaID.init({ restoreUrl: false })
        this.syncStore()
      }
      store.setAuthError('')
      return Boolean(store.token)
    } catch (error) {
      await mechtaID.clearToken()
      store.clearSSO()
      store.setAuthError(authErrorMessage(error))
      if (isAuthorizationCallback()) restoreHashRoute('/')
      return false
    }
  }

  private syncStore(): MechtaIDUser | null {
    const user = mechtaID.user
    if (user) authStore().setSSOUser(user)
    else authStore().clearSSO()
    return user
  }
}

export const mechtaIDAuthService = new MechtaIDAuthService()
